import { create } from 'zustand';
import { currentLanguage, loadLanguage, persistLanguage, type UiLanguage } from '../i18n';
import { appliquerSens } from '../i18n/sens';

interface LanguageState {
  language: UiLanguage;
  // `false` tant que la préférence enregistrée n'a pas été lue : l'application attend, plutôt que
  // d'afficher un écran en français qui basculerait en anglais une frame plus tard.
  ready: boolean;
  load: () => Promise<void>;
  change: (language: UiLanguage) => Promise<void>;
}

/**
 * Langue de l'INTERFACE, côté React.
 *
 * i18n-js garde la locale dans une variable de module : la changer ne re-rend rien. Ce store existe
 * pour ça — App s'en sert comme CLÉ de l'arbre de navigation, si bien qu'un changement de langue
 * re-monte l'application entière. C'est plus brutal qu'un abonnement par composant, et c'est
 * exactement ce qu'on veut : aucun écran n'a à savoir qu'il est traduisible, et il ne peut pas
 * rester un fragment dans l'ancienne langue.
 *
 * Le store ne stocke PAS les traductions : elles restent dans le module i18n, accessible hors React
 * (services, hooks, formatteurs).
 */
export const useLanguageStore = create<LanguageState>((set) => ({
  language: currentLanguage(),
  ready: false,
  load: async () => {
    // `ready` passe à true dans TOUS les cas : une exception inattendue ici laisserait
    // l'application sur son écran d'attente, définitivement. Le pire scénario est un démarrage en
    // français, jamais un blocage.
    try {
      const language = await loadLanguage();
      // Arabe : de droite à gauche. Si le sens enregistré diffère, l'application se relance ici.
      await appliquerSens(language);
      set({ language, ready: true });
    } catch {
      set({ language: 'fr', ready: true });
    }
  },
  change: async (language) => {
    await persistLanguage(language);
    // Passer à l'arabe, ou le quitter, change le sens d'écriture : l'application se relance.
    await appliquerSens(language);
    set({ language });
  },
}));
